import { gameImages } from './assets';

export interface SkillSetGameDetail {
  readonly gameName: string;
  readonly gameDescriptions: readonly string[];
  readonly challenge: string;
  readonly rule: string;
  readonly example: readonly string[];
  readonly image: string;
}

export interface SkillSetDetail {
  readonly label: string;
  readonly text: string;
  readonly games: readonly SkillSetGameDetail[];
}

export const skillSetGameDetails: readonly SkillSetDetail[] = [
  {
    label: 'Skill Set 1',
    text: 'Gain proficiency in addition and subtraction facts for numbers 1 through 12, improve numerical fluency and critical-thinking skills.',
    games: [
      {
        gameName: 'Add Only 1 Dot',
        gameDescriptions: ['One-step addition using numbers 0 through 12.'],
        challenge: 'Solve four cards in 60 seconds to jump to game 2.',
        rule: 'Pick the wheel that makes the target number (shown in white) using addition ONLY. Use all numbers on the wheel, but use each number only once.',
        example: ['Target number is 6. ', 'The left wheel works.', '5 + 1 = 6'],
        image: gameImages.game11,
      },
      {
        gameName: 'Subtract Only 1 Dot',
        gameDescriptions: ['One-step subtraction using numbers 0 through 12. '],
        challenge: 'Solve four cards in 60 seconds to jump to game 3.',
        rule: 'Pick the wheel that makes the target number (shown in white) using subtraction ONLY. Use all numbers on the wheel, but use each number only once.',
        example: [
          'Target number is 3. ',
          'The right wheel works.',
          '5 - 2 = 3',
        ],
        image: gameImages.game12,
      },
      {
        gameName: 'Add and Subtract Only 2 Dot',
        gameDescriptions: [
          'Randomly displays Add Only or Subtract Only. One-step, using numbers 0 through 24.',
        ],
        challenge: 'Solve four cards in 60 seconds to jump to Skill Set 2.',
        rule: 'Pick the wheel that makes the target number (shown in white) using addition or subtraction ONLY. (Cards rotate at random.) Use all numbers on the wheel, but use each number only once.',
        example: [
          'Target number is 13. ',
          'The left wheel works.',
          '17 - 4 = 13',
        ],
        image: gameImages.game13,
      },
    ],
  },
  {
    label: 'Skill Set 2',
    text: 'Practice addition and subtraction facts, learn to combine three numbers by using grouping skills, and begin to recognize patterns.',
    games: [
      {
        gameName: 'Add/Subtract 1 Dot',
        gameDescriptions: [
          'One-step addition or subtraction using numbers 0 to 24.',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). You can add or subtract. Use all numbers on the wheel, but use each number only once. Example: Target number is 16. The left wheel works. 20 – 4 = 16',
        rule: 'One-step addition or subtraction using numbers 0 to 24. Solve four cards in 60 seconds to jump to game 2.',
        example: [
          'Target number is 16.',
          'The left wheel works.',
          '20 – 4 = 16',
        ],
        image: gameImages.game21,
      },
      {
        gameName: 'Add/Subtract 2 Dot',
        gameDescriptions: [
          "Two-step addition and subtraction using numbers 0 to 24. Builds proficiency in adding and subtracting single- and double-digit whole numbers. This game contains 'grouping hints' which appear when the card is in play.",
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). You can add or subtract. Use all numbers on the wheel, but use each number only once. (Hint shown in first round.) Example: Target number is 8. The left wheel works. 5 – 4 = 1 9 – 1 = 8',
        rule: 'Solve three cards in 60 seconds to jump to game 3.',
        example: [
          'Target number is 8.',
          'The left wheel works.',
          '5 – 4 = 1',
          '9 – 1 = 8',
        ],
        image: gameImages.game22,
      },
      {
        gameName: 'Add/Subtract 3 Dot',
        gameDescriptions: [
          'Two-step addition and subtraction. Target number is always 24 to refine pattern recognition.',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). Target number is always 24 to refine pattern recognition. You can add or subtract. Use all numbers on the wheel, but use each number only once. Example: Target number is 24. The left wheel works. 13 – 1 = 12 12 + 12 = 24',
        rule: 'Solve three cards in 60 seconds to jump to Skill Set 3.',
        example: [
          'Target number is 24.',
          'The left wheel works.',
          '13 – 1 = 12',
          '12 + 12 = 24',
        ],
        image: gameImages.game23,
      },
    ],
  },
  {
    label: 'Skill Set 3',
    text: 'Solidify multiplication and division facts for fact families 1 through 10. Practice and review all four operations (+, –, x, ÷). Improve numerical fluency and problem-solving skills.',
    games: [
      {
        gameName: 'Multiply Only 1 & 2 Dot',
        gameDescriptions: [
          '1 Dot: One-step multiplication using fact families 1, 2, 5 and 10. ',
          '2 Dot: One-step multiplication using fact families 3, 4, 6, 7, 8 and 9.',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white) using multiplication only. Use all numbers on the wheel, but use each number only once. Example: Target number is 12. The left wheel works. 2 x 6 = 12',
        rule: 'Solve three 2 Dot cards in 60 seconds to jump to game 2.',
        example: [
          'Target number is 12.',
          'The left wheel works.',
          '2 x 6 = 12',
        ],
        image: gameImages.game31,
      },
      {
        gameName: 'Divide Only 1 & 2 Dot',
        gameDescriptions: [
          '1 Dot: One-step division using fact families 1, 2, 5 and 10. ',
          '2 Dot: One-step division using fact families 3, 4, 6, 7, 8 and 9.',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white) using division only. Use all numbers on the wheel, but use each number only once. Example: Target number is 7. The right wheel works. 7 ÷ 1 = 7',
        rule: 'Solve three 2 Dot cards in 60 seconds to jump to game 3.',
        example: ['Target number is 7.', 'The right wheel works.', '7 ÷ 1 = 7'],
        image: gameImages.game32,
      },
      {
        gameName: 'Multiply/Divide 1 Dot',
        gameDescriptions: [
          'One-step addition, subtraction, multiplication or division of single- and double-digit numbers.',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). You can add, subtract, multiply or divide. Use all numbers on the wheel, but use each number only once. Example: Target number is 24. The right wheel works. 6 x 4 = 24',
        rule: 'Solve four cards in 60 seconds to jump to Skill Set 4.',
        example: [
          'Target number is 24.',
          'The right wheel works.',
          '6 x 4 = 24',
        ],
        image: gameImages.game33,
      },
    ],
  },
  {
    label: 'Skill Set 4',
    text: 'Strengthen numerical fluency, gain speed, accuracy & proficiency in all four operations (+, –, x, ÷). Learn to combine four numbers, recognize patterns, practice order of operations, engage in critical thinking and refine problem-solving skills.',
    games: [
      {
        gameName: 'Multiply/Divide 2 Dot',
        gameDescriptions: [
          "Two-step addition, subtraction, multiplication and division of single- and double-digit numbers. Solidify knowledge of the multiplication table. (A 'hint' appears during the first round; during the second round the hint disappears.)",
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). You can add, subtract, multiply or divide. Use all numbers on the wheel, but use each number only once. (Hint shown in first round.) Example: Target number is 6. The left wheel works. 8 ÷ 2 = 4 4 + 2 = 6',
        rule: 'Solve three cards in 60 seconds to jump to game 2.',
        example: [
          'Target number is 6.',
          'The left wheel works.',
          '8 ÷ 2 = 4',
          '4 + 2 = 6',
        ],
        image: gameImages.game41,
      },
      {
        gameName: 'Multiply/Divide 3 Dot',
        gameDescriptions: [
          'Two-step addition, subtraction, multiplication and division of single- and double-digit numbers. Target number is always 24 to reinforce pattern recognition.',
        ],
        challenge:
          'Pick the wheel that makes the target number 24. You can add, subtract, multiply or divide. Use all numbers on the wheel, but use each number only once. Example: Target number is 24. The left wheel works. 3 x 7 = 21 21 + 3 = 24',
        rule: 'Solve three cards in 60 seconds to jump to game 3.',
        example: [
          'Target number is 24.',
          'The left wheel works.',
          '3 x 7 = 21',
          '21 + 3 = 24',
        ],
        image: gameImages.game42,
      },
      {
        gameName: 'Single Digits 1 & 2 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division with whole numbers 1 through 9. Target number is 24. 1 Dot is easier, 2 Dot more challenging. Develop critical-thinking skills, pattern recognition and numerical fluency.',
        ],
        challenge:
          'Make the target number 24. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24. 7 – 5 = 2 2 + 1 = 3 3 x 8 = 24',
        rule: 'Solve two 2 Dot cards in 60 seconds to jump to Skill Set 5.',
        example: [
          'Target number is 24.',
          '7 – 5 = 2',
          '2 + 1 = 3',
          '3 x 8 = 24',
        ],
        image: gameImages.game43,
      },
    ],
  },
  {
    label: 'Skill Set 5',
    text: 'Increase numerical fluency and proficiency in using all four operations (with an emphasis on double-digit numbers). Begin adding and subtracting fractions with like and unlike denominators and reducing them to their simplest form.',
    games: [
      {
        gameName: 'Fractions Primer (+ –) 1 & 2 Dot',
        gameDescriptions: [
          'One-step addition and subtraction of fractions with like and unlike denominators; 1 Dot cards have like denominators, 2 Dot cards have unlike denominators. Reduce fractions to their simplest form.',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). You can add or subtract only. Use all numbers on the wheel, but use each number only once. Example: Target number is 2/3. 11/12  –  3/12  =  2/3',
        rule: 'Solve two 2 Dot cards in 90 seconds to jump to game 2.',
        example: ['Target number is 2/3.', '11/12  –  3/12  =  2/3'],
        image: gameImages.game51,
      },
      {
        gameName: 'Double Digits 1 & 2 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division with whole numbers 1 through 24. Target number is 24. 1 Dot is easier, 2 Dot more challenging. Refine pattern recognition, critical-thinking skills, and numerical fluency.',
        ],
        challenge:
          'Make the target number 24. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24. 11 + 11 = 22 4 – 2 = 2 22 + 2 = 24',
        rule: 'Solve two 2 Dot cards in 90 seconds to jump to game 3.',
        example: [
          'Target number is 24.',
          '11 + 11 = 22',
          '4 – 2 = 2',
          '22 + 2 = 24',
        ],
        image: gameImages.game52,
      },
      {
        gameName: 'Decimals Primer 1 & 2 Dot',
        gameDescriptions: [
          'One-step (1 Dot) and two-step (2 Dot) process. Use all four operations with decimals; Student determines which operation will be needed to correctly solve game card for the target numbers .05, .1, .2, .25, .3, .4, .5, .6, .7, .75, .8, .9',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). You can add, subtract, multiply or divide. Use all numbers on the wheel, but use each number only once. Example: Target number is 3. The right wheel works. 2.7  +  0.3  =  3',
        rule: 'Solve two 2 Dot cards in 90 seconds to jump to Skill Set 6.',
        example: [
          'Target number is 3.',
          'The right wheel works.',
          '2.7  +  0.3  =  3',
        ],
        image: gameImages.game53,
      },
    ],
  },
  {
    label: 'Skill Set 6',
    text: 'Master numerical fluency. Use all four operations (+, –, x, ÷) with whole numbers, decimals, and fractions. Introduce pre-algebra by finding a value for a variable number. Encourages higher-order thinking skills.',
    games: [
      {
        gameName: 'Decimals 1 & 2 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division involving decimals.',
          '1 Dot is easier, 2 Dot more challenging.',
        ],
        challenge:
          'Make the target number 24. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24. 0.4  +  0.6  =  1 1  +  1  =  2 2  x  12  =  24',
        rule: 'Solve two 2 Dot cards in 60 seconds to jump to game 2.',
        example: [
          'Target number is 24.',
          '0.4  +  0.6  =  1',
          '1  +  1  =  2',
          '2  x  12  =  24',
        ],
        image: gameImages.game61,
      },
      {
        gameName: 'Variables',
        gameDescriptions: [
          'Multi-step operation introducing pre-algebra. Find a value for a variable number. Encourages thinking along simultaneous tracks in problem solving.',
        ],
        challenge:
          'Pick a variable number from 1 to 9 which, when used with other numbers in each wheel, can make the target number 24 on both wheels. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. (Solve left first) Example: Target number is 24. Variable number can be 5 Left Wheel: 5 x 5 = 25 25 – 1 = 24 24 x 1 = 24 Right Wheel: 5 x 3 = 15 15 + 1 = 16 16 + 8 = 24',
        rule: 'Solve two cards in 60 seconds to jump to game 3.',
        example: [
          'Target number is 24.    Variable number can be 5',
          'Left Wheel:                    Right Wheel:',
          '5 x 5 = 25                        5 x 3 = 15',
          '25 – 1 = 24                      15 + 1 = 16',
          '24 x 1 = 24                      16 + 8 = 24',
        ],
        image: gameImages.game62,
      },
      {
        gameName: 'Fractions Primer (X ÷) 1 & 2 Dot',
        gameDescriptions: [
          'One- and two-step multiplication and division of fractions and whole numbers.',
          '1 Dot: One-step problem using all operations with fractions and whole numbers.',
          '2 Dot: Two-step problem using all operations with fractions and whole numbers.',
        ],
        challenge:
          'Pick the wheel that makes the target number (shown in white). You can add, subtract, multiply or divide. Use all numbers on the wheel, but use each number only once. Example: Target number is 6. The right wheel works. 3  ÷  1/2  = 6',
        rule: 'Solve two 2 Dot cards in 60 seconds to jump to Skill Set 7.',
        example: [
          'Target number is 6.',
          'The right wheel works.',
          '3  ÷  1/2  = 6',
        ],
        image: gameImages.game63,
      },
    ],
  },
  {
    label: 'Skill Set 7',
    text: 'Focuses on refining the manipulation of fractions and whole numbers, begin using integers (positive and negative numbers) and algebraic expressions (x,y). Continue to practice problem solving, pattern recognition, sequential thinking, concentration and numerical sense.',
    games: [
      {
        gameName: 'Fractions 1 & 2 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division with whole numbers (1 through 9) and fractions.',
          'Target number is 24. 1 Dot easier, 2 Dot more challenging.',
        ],
        challenge:
          'Make the target number 24. You can add, subtract, multiply or divide. Use all numbers on the wheel, but use each number only once. Example: Target number is 24. 4  ÷  1/3  =  12 2  x  1  =  2 2  x  12  =  24',
        rule: 'Solve 2 cards in 60 seconds to jump to game 2.',
        example: [
          'Target number is 24.',
          '4  ÷  1/3  =  12',
          '2  x  1  =  2',
          '2  x  12  =  24',
        ],
        image: gameImages.game71,
      },
      {
        gameName: 'Integers 1 & 2 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division using single-digit integers (positive and negative numbers).',
          'Target number is 24. 1 Dot easier, 2 Dot more challenging.',
        ],
        challenge:
          'Make the target number positive 24. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24. –4  +  –8  =  –12 2  –  4  =  –2 –2  x  –12  =  24',
        rule: 'Solve 2 cards in 60 seconds to jump to game 3.',
        example: [
          'Target number is 24.',
          '–4  +  –8  =  –12',
          '2  –  4  =  –2',
          '–2  x  –12  =  24',
        ],
        image: gameImages.game72,
      },
      {
        gameName: 'Algebra 1 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division of single-digit numbers using algebraic notation.',
          'Target number is 24. x and or y can be any whole number, 2 to 9.',
        ],
        challenge:
          'Find a value for x or y (any whole number) which, when used with the other numbers on the card, makes the target number 24. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24.   x = 4   (2/4 = 1/2) 4  x  5  =  20 8  x  1/2  =  4 20  +  4  =  24',
        rule: 'Solve 3 cards in 90 seconds to jump to Skill Set 8.',
        example: [
          'Target number is 24.   x = 4   (2/4 = 1/2)',
          '4  x  5  =  20',
          '8  x  1/2  =  4',
          '20  +  4  =  24',
        ],
        image: gameImages.game73,
      },
    ],
  },
  {
    label: 'Skill Set 8',
    text: 'Focuses on simple exponents, roots, simplifying factoring and evaluating algebraic expressions using one or two variables. Expressions increase in difficulty from 2 to 3 Dot cards. Continue to practice problem solving, pattern recognition, sequential thinking, simultaneous thinking, concentration and number sense.',
    games: [
      {
        gameName: 'Exponents 1 & 2 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division using exponential operations.',
          'Target number is 24. 1 Dot easier, 2 Dot more challenging.',
        ],
        challenge:
          'Make the target number 24. You can add, subtract, multiply or divide, and you MUST use ONLY ONE exponential operation. Square, cube, square root, cube root. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24.   √– of 9 = 3 3 – 1 = 2 2 x 1 = 2 2 x 12 = 24',
        rule: 'Solve two 2 Dot cards in 90 seconds to jump to game 2.',
        example: [
          'Target number is 24.   √– of 9 = 3',
          '3 – 1 = 2',
          '2 x 1 = 2',
          '2 x 12 = 24',
        ],
        image: gameImages.game81,
      },
      {
        gameName: 'Algebra 2 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division using algebraic notation. Students must simplify algebraic expressions before solving the card—a two-step process.',
          'Target number is 24. 2 Dot Algebra cards are more complex than cards found in Skill Set 7.',
        ],
        challenge:
          'Find a value for x or y (any whole number) which, when used with the other numbers on the card, makes the target number 24. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24.     y = 2    [4 x 2 – 5 = 3] 3 x 2 = 6 5 – 1 - 4 6 x 4 = 24',
        rule: 'Solve 2 cards in 90 seconds to jump to game 3.',
        example: [
          'Target number is 24.     y = 2    [4 x 2 – 5 = 3]',
          '3 x 2 = 6',
          '5 – 1 - 4',
          '6 x 4 = 24',
        ],
        image: gameImages.game82,
      },
      {
        gameName: 'Algebra 3 Dot',
        gameDescriptions: [
          'Multi-step addition, subtraction, multiplication and division using algebraic notation. Students must simplify algebraic expressions before solving the card—a two-step process.',
          'Target number is 24. May contain more than one algebraic expression and fractions containing x and or y.',
        ],
        challenge:
          'Find a value for x and y (any whole number) which, when used with the other numbers on the card, makes the target number 24. You can add, subtract, multiply or divide. Use all four numbers on the wheel, but use each number only once. Example: Target number is 24.     y = 2 (y+3)(y2-2)/(y+3) = y2 - 2 = 4 - 2 = 2 4  –  2  =  2 4  x  3  =  12 12  x  2  =  24',
        rule: 'Solve 2 cards in 90 seconds and you have completed all Skill Set games in the First In Math® Online Program!!!',
        example: [
          'Target number is 24.     y = 2',
          '(y+3)(y2-2)/(y+3) = y2 - 2 = 4 - 2 = 2',
          '4  –  2  =  2',
          '4  x  3  =  12',
          '12  x  2  =  24',
        ],
        image: gameImages.game83,
      },
    ],
  },
];
